import { createBrowserRouter, Navigate } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import { AppLayout } from '@/components/layout/AppLayout';
import { ProtectedRoute } from '@/components/layout/ProtectedRoute';
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import { ROUTES } from '@/lib/constants/routes';
import { ELECTRONIC_SIGNATURE_ENABLED } from '@/lib/constants/featureFlags';

// Lazy load all pages for code splitting
// Auth pages
const LoginPage = lazy(() => import('@/features/auth/pages/LoginPage').then(m => ({ default: m.LoginPage })));
const RegistrationPage = lazy(() => import('@/features/auth/pages/RegistrationPage').then(m => ({ default: m.RegistrationPage })));
const RecoveryPage = lazy(() => import('@/features/auth/pages/RecoveryPage').then(m => ({ default: m.RecoveryPage })));
const VerificationPage = lazy(() => import('@/features/auth/pages/VerificationPage').then(m => ({ default: m.VerificationPage })));
const SettingsPage = lazy(() => import('@/features/auth/pages/SettingsPage').then(m => ({ default: m.SettingsPage })));

// Feature pages
const DashboardPage = lazy(() => import('@/features/dashboard/pages/DashboardPage').then(m => ({ default: m.DashboardPage })));
const WelcomePage = lazy(() => import('@/pages/WelcomePage').then(m => ({ default: m.WelcomePage })));

// Projects pages
const ProjectsPage = lazy(() => import('@/features/projects/pages/ProjectsPage').then(m => ({ default: m.ProjectsPage })));

// Organization Settings pages (members, security, SSO, danger zone)
const OrganizationSettingsPage = lazy(() => import('@/features/organization-settings/pages/OrganizationSettingsPage').then(m => ({ default: m.OrganizationSettingsPage })));

// Account-level (per-user) settings
const AccountSecurityPage = lazy(() => import('@/features/account/pages/AccountSecurityPage').then(m => ({ default: m.AccountSecurityPage })));

// Project Settings pages
const ProjectSettingsPage = lazy(() => import('@/features/project-settings/pages/ProjectSettingsPage').then(m => ({ default: m.ProjectSettingsPage })));

// Electronic Signature pages — declared inside the feature flag so
// production builds tree-shake the pages and emit no chunks for them.
const electronicSignaturePages = ELECTRONIC_SIGNATURE_ENABLED
  ? {
      Settings: lazy(() => import('@/features/electronic-signature/pages/ElectronicSignatureSettingsPage').then(m => ({ default: m.ElectronicSignatureSettingsPage }))),
      Metrics: lazy(() => import('@/features/electronic-signature/pages/ElectronicSignatureMetricsPage').then(m => ({ default: m.ElectronicSignatureMetricsPage }))),
      List: lazy(() => import('@/features/electronic-signature/pages/ElectronicSignatureListPage').then(m => ({ default: m.ElectronicSignatureListPage }))),
    }
  : null;

// Tables pages
const IdentityValidationPage = lazy(() => import('@/features/tables/pages/IdentityValidationPage').then(m => ({ default: m.IdentityValidationPage })));
const IdentityValidationDetailPage = lazy(() => import('@/features/tables/pages/IdentityValidationDetailPage').then(m => ({ default: m.IdentityValidationDetailPage })));

// Face Recognition pages
const FaceRecognitionMetricsPage = lazy(() => 
  import('@/features/dashboard/pages/FaceRecognitionMetricsPage').then(m => ({ default: m.FaceRecognitionMetricsPage }))
);
const FaceRecognitionOperationsPage = lazy(() => 
  import('@/features/tables/pages/FaceRecognitionOperationsPage').then(m => ({ default: m.FaceRecognitionOperationsPage }))
);
const FaceRecognitionOperationDetailPage = lazy(() =>
  import('@/features/tables/pages/FaceRecognitionOperationDetailPage').then(m => ({ default: m.FaceRecognitionOperationDetailPage }))
);

// Documentation pages (developer portal)
const DocsIndexPage = lazy(() =>
  import('@/features/docs/pages/DocsIndexPage').then(m => ({ default: m.DocsIndexPage }))
);
const DocsSectionPage = lazy(() =>
  import('@/features/docs/pages/DocsSectionPage').then(m => ({ default: m.DocsSectionPage }))
);
const DocsArticlePage = lazy(() =>
  import('@/features/docs/pages/DocsArticlePage').then(m => ({ default: m.DocsArticlePage }))
);

// Loading fallback component
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-[400px]">
    <LoadingSpinner size="lg" />
  </div>
);

// Wrapper component for lazy loaded pages
const LazyPageWrapper = ({ children }: { children: React.ReactNode }) => (
  <Suspense fallback={<PageLoader />}>
    {children}
  </Suspense>
);

/**
 * Application router configuration
 * 
 * This file defines all routes for the application.
 * Routes are organized into:
 * - Public routes (auth pages)
 * - Protected routes (authenticated pages with AppLayout)
 * 
 * Protected routes use the ProtectedRoute component to ensure authentication.
 * Unauthenticated users are redirected to the login page.
 */

export const router = createBrowserRouter([
  // Public routes - Authentication
  {
    path: '/auth',
    children: [
      { path: 'login', element: <LazyPageWrapper><LoginPage /></LazyPageWrapper> },
      { path: 'registration', element: <LazyPageWrapper><RegistrationPage /></LazyPageWrapper> },
      { path: 'recovery', element: <LazyPageWrapper><RecoveryPage /></LazyPageWrapper> },
      { path: 'verification', element: <LazyPageWrapper><VerificationPage /></LazyPageWrapper> },
      { path: 'settings', element: <LazyPageWrapper><SettingsPage /></LazyPageWrapper> },
    ],
  },

  // Protected routes - Main application
  {
    path: '/',
    element: <ProtectedRoute />,
    children: [
      {
        path: '',
        element: <AppLayout />,
        children: [
      // Redirect root to dashboard
      { index: true, element: <Navigate to={ROUTES.DASHBOARD} replace /> },
      
      // Dashboard
      { path: 'dashboard', element: <LazyPageWrapper><DashboardPage /></LazyPageWrapper> },
      
          // Face Recognition Metrics (under dashboard)
          { path: 'dashboard/face-recognition', element: <LazyPageWrapper><FaceRecognitionMetricsPage /></LazyPageWrapper> },
          
          // Welcome page (for users with no projects)
          { path: 'welcome', element: <LazyPageWrapper><WelcomePage /></LazyPageWrapper> },
          
          // Projects
          { path: 'projects', element: <LazyPageWrapper><ProjectsPage /></LazyPageWrapper> },
          
          // Organization Settings (members, security, SSO, danger zone)
          {
            path: 'organization-settings',
            element: <LazyPageWrapper><OrganizationSettingsPage /></LazyPageWrapper>,
          },

          // Account-level security: password change, passkey enrolment, TOTP,
          // backup codes. Entry from the user menu — distinct from
          // /organization-settings/security which is org-policy.
          {
            path: 'account/security',
            element: <LazyPageWrapper><AccountSecurityPage /></LazyPageWrapper>,
          },

          // Project Settings
          {
            path: 'project-settings',
            element: <LazyPageWrapper><ProjectSettingsPage /></LazyPageWrapper>,
          },

          // Electronic Signature (Firma Electrónica) — hidden in production
          // while in development (see featureFlags.ts).
          ...(electronicSignaturePages
            ? [
                {
                  path: 'electronic-signature',
                  children: [
                    { path: 'metrics', element: <LazyPageWrapper><electronicSignaturePages.Metrics /></LazyPageWrapper> },
                    { path: 'list', element: <LazyPageWrapper><electronicSignaturePages.List /></LazyPageWrapper> },
                    { path: 'settings', element: <LazyPageWrapper><electronicSignaturePages.Settings /></LazyPageWrapper> },
                  ],
                },
              ]
            : []),

          // Documentation (developer portal)
          {
            path: 'docs',
            children: [
              { index: true, element: <LazyPageWrapper><DocsIndexPage /></LazyPageWrapper> },
              { path: ':section', element: <LazyPageWrapper><DocsSectionPage /></LazyPageWrapper> },
              { path: ':section/:slug', element: <LazyPageWrapper><DocsArticlePage /></LazyPageWrapper> },
            ],
          },

          // Tables
          {
            path: 'tables',
            children: [
              { path: 'identity-validation', element: <LazyPageWrapper><IdentityValidationPage /></LazyPageWrapper> },
              { path: 'identity-validation/:id', element: <LazyPageWrapper><IdentityValidationDetailPage /></LazyPageWrapper> },
              { path: 'face-recognition', element: <LazyPageWrapper><FaceRecognitionOperationsPage /></LazyPageWrapper> },
              { path: 'face-recognition/:operationId', element: <LazyPageWrapper><FaceRecognitionOperationDetailPage /></LazyPageWrapper> },
              {
                path: 'idp',
                children: [
                  { path: 'identities-list', element: <div className="p-6">Identities List - Coming in task 28</div> },
                  { path: 'user-profile', element: <div className="p-6">User Profile - Coming in task 28</div> },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
]);
